import { useParams } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { JournalEntry } from "@/apis/gql/types";
import { DateCell } from "@/components/shared/cells/date-cell";
import { SelectCell } from "@/components/shared/cells/select";
import { RawTextCell } from "@/components/shared/cells/text";
import { SignedAmount } from "@/components/shared/miscellaneous/signed-amount";
import { SelectHeader } from "@/components/shared/select-inputs/select";
import { DataTableColumnHeader } from "@/components/shared/tables/ColumnHeader";
import { Button } from "@/components/ui/button";
import { AccountEntityType } from "@/lib/constants/account-types";
import getAccountTypeIcon from "@/lib/miscellaneous/get-account-type-icon";
import { cn } from "@/lib/utils";

const selectColumn: ColumnDef<JournalEntry> = {
  id: "select",
  size: 45,
  header: (header) => <SelectHeader {...header} />,
  cell: (cell) => {
    return (
      <div className="flex items-center justify-center h-full w-full">
        <SelectCell {...cell} />
      </div>
    );
  },
  enableSorting: false,
  enableHiding: false,
};

const timestampColumn: ColumnDef<JournalEntry> = {
  accessorKey: "timestamp",
  id: "timestamp",
  size: 100,
  header: (header) => {
    const { t } = useTranslation();
    return <DataTableColumnHeader column={header.column} title={t("date")} />;
  },
  cell: ({ row }) => {
    if (!row.original.timestamp) {
      return null;
    }

    return (
      <div className="p-1">
        <DateCell value={row.original.timestamp} format="dd.MM.yyyy" />
      </div>
    );
  },
};

const bankAccountColumn: ColumnDef<JournalEntry> = {
  accessorKey: "bankAccount",
  id: "bankAccount",
  size: 220,
  header: (header) => (
    <DataTableColumnHeader column={header.column} title="Konto" />
  ),
  cell: ({ row }) => {
    const entry = row.original;

    const bankAccountLine = entry.journalLines.find(
      (line) =>
        line.journalAccount?.entityType === AccountEntityType.BANK_ACCOUNT,
    );

    if (!bankAccountLine?.journalAccount?.entity) {
      return <p className="text-gray-500 p-1">—</p>;
    }

    const entity = bankAccountLine.journalAccount.entity;

    if (entity.__typename === "BankAccount") {
      const bank = entity.bankConnection?.metadata?.bank;
      const AccountIcon = getAccountTypeIcon(entity.type);

      return (
        <div className="flex items-center space-x-2 p-1">
          <div className="relative h-8 w-8 rounded-lg flex items-center justify-center border border-gray-200 bg-gray-50 flex-shrink-0">
            <AccountIcon className="h-4 w-4 text-gray-600" />
            {bank?.icon?.url && (
              <div className="absolute right-[-6px] bottom-[-4px] h-5 w-5 bg-white rounded border border-gray-200 flex items-center justify-center overflow-hidden box-border p-0.5">
                <img
                  src={bank.icon?.url}
                  alt={bank.name}
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            )}
          </div>
          <RawTextCell value={entity.name ?? ""} />
        </div>
      );
    }

    return <p className="text-gray-500 p-1">—</p>;
  },
};

const supplierColumn: ColumnDef<JournalEntry> = {
  accessorKey: "counterpartyName",
  id: "counterpartyName",
  size: 220,
  header: (header) => (
    <DataTableColumnHeader column={header.column} title="Geschäftspartner" />
  ),
  cell: ({ row }) => {
    const entry = row.original;

    if (entry.document?.__typename === "Transaction") {
      return (
        <div className="p-1">
          <RawTextCell value={entry.document.counterpartyName ?? ""} />
        </div>
      );
    }

    return <p className="text-gray-500 p-1">—</p>;
  },
};

const amountColumn: ColumnDef<JournalEntry> = {
  accessorKey: "amount",
  id: "amount",
  size: 160,
  header: (header) => (
    <DataTableColumnHeader column={header.column} title="Gesamtbetrag" />
  ),
  cell: ({ row }) => {
    const entry = row.original;

    if (entry.document?.__typename === "Transaction") {
      return (
        <SignedAmount
          amount={entry.document.amount}
          currency={entry.document.currency ?? "EUR"}
          side={entry.document.side}
        />
      );
    }

    return null;
  },
};

const buColumn: ColumnDef<JournalEntry> = {
  accessorKey: "bu",
  id: "bu",
  size: 80,
  header: (header) => (
    <DataTableColumnHeader column={header.column} title="BU" />
  ),
  cell: ({ row }) => {
    const entry = row.original;

    const taxCodes = entry.journalLines
      .map((line) => line.taxCode)
      .filter(
        (taxCode): taxCode is NonNullable<typeof taxCode> => taxCode !== null,
      );

    const uniqueTaxCodes = Array.from(
      new Set(taxCodes.map((taxCode) => taxCode.datevBu)),
    );

    const vatRates = uniqueTaxCodes
      .map((bu) => bu?.toString() ?? "")
      .filter(Boolean)
      .join(", ");

    if (vatRates === "") {
      return <p className="text-gray-400 p-1 text-center">—</p>;
    }

    return (
      <div className="p-1">
        <RawTextCell
          className="font-mono text-xs text-center"
          value={vatRates}
        />
      </div>
    );
  },
};

const documentNumberColumn: ColumnDef<JournalEntry> = {
  accessorKey: "documentNumber",
  id: "documentNumber",
  size: 220,
  header: (header) => (
    <DataTableColumnHeader column={header.column} title="Belegnr." />
  ),
  cell: ({ row }) => {
    return (
      <div className="p-1">
        <RawTextCell
          value={row.original.documentNumber ?? "—"}
          className={cn(row.original.documentNumber ? "" : "text-gray-400")}
        />
      </div>
    );
  },
};

const bookingTextColumn: ColumnDef<JournalEntry> = {
  accessorKey: "bookingText",
  id: "bookingText",
  size: 220,
  header: (header) => (
    <DataTableColumnHeader column={header.column} title="Buchungstext" />
  ),
  cell: ({ row }) => {
    return (
      <div className="p-1">
        <RawTextCell
          value={row.original.bookingText || "—"}
          className={cn(row.original.bookingText ? "" : "text-gray-400")}
        />
      </div>
    );
  },
};

const accountColumn: ColumnDef<JournalEntry> = {
  accessorKey: "account",
  id: "account",
  size: 250,
  header: (header) => {
    const { t } = useTranslation();
    return (
      <DataTableColumnHeader
        column={header.column}
        title={t("counter_account")}
      />
    );
  },
  cell: ({ row }) => {
    const entry = row.original;

    const counterAccounts = entry.journalLines
      .map((line) => line.journalAccount)
      .filter(
        (account) => account?.entityType !== AccountEntityType.BANK_ACCOUNT,
      )
      .sort((a, b) => {
        if (a?.entityType === AccountEntityType.COUNTERPARTY) return -1;
        if (b?.entityType === AccountEntityType.COUNTERPARTY) return 1;
        return 0;
      });

    return (
      <div className="flex-1 flex flex-col overflow-hidden min-w-0 px-1">
        <div className="min-w-0">
          <RawTextCell
            className="text-left"
            value={counterAccounts.map((account) => account?.name).join(", ")}
          />
        </div>
        <div className="text-gray-500 text-xs min-w-0">
          <RawTextCell
            value={counterAccounts.map((account) => account?.number).join(", ")}
          />
        </div>
      </div>
    );
  },
};

const linkColumn: ColumnDef<JournalEntry> = {
  accessorKey: "link",
  id: "link",
  size: 50,
  header: () => null,
  cell: ({ row }) => {
    const { company: companySlug } = useParams({ strict: false });

    const handleNavigate = () => {
      const entry = row.original;
      let link = "";

      if (entry.document?.__typename === "Transaction") {
        link = `/${companySlug}/buchhaltung/transaktionen/?sort=bookingDate&order=desc&selected-transaction-ids=${entry.document.bankTxnId}&global_search=${entry.document.bankTxnId}`;
      }

      if (link) {
        window.open(link, "_blank");
      }
    };

    return (
      <div className="p-1 flex items-center justify-center">
        <Button variant="ghost" size="icon" onClick={handleNavigate}>
          <ExternalLink className="h-4 w-4 text-gray-500" />
        </Button>
      </div>
    );
  },
};

export const columns: ColumnDef<JournalEntry>[] = [
  selectColumn,
  timestampColumn,
  bankAccountColumn,
  supplierColumn,
  amountColumn,
  accountColumn,
  buColumn,
  documentNumberColumn,
  bookingTextColumn,
  linkColumn,
];
